
import { toast } from "react-toastify";
// import { FormFieldErrors, ParsedFormFieldErrors } from "../types";
import CryptoJS from 'crypto-js';
import { SavedAddress } from "types";

export const textToTitleCase = (text: string): string => {
    return text
        ?.toLowerCase() // Convert entire string to lowercase to standardize
        .split(' ') // Split the string into words
        .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
        .join(' '); // Join the words back into a single string
};

export const arrayOfUniqueStrings = (arr: string[]): string[] => {
    return Array.from(new Set(arr));
}

export const sanitizeURLs = (text: string): string => {
    // Fix scheme-less href="www...." so Capacitor doesn't resolve against localhost
    const withAbsoluteHrefs = (text ?? '').replace(
        /href=(["'])(www\.[^"']+)\1/gi,
        'href=$1https://$2$1'
    );
    const urlRegex = /((https?:\/\/|www\.)[^\s]+)/g; // Regex to match URLs
    return withAbsoluteHrefs.replace(urlRegex, (url) => {
        let link = url;
        if (!link.startsWith("http")) {
            link = "https://" + link; // Add https if missing
        }
        return `<a href="${link}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
}

export const getErrorMessage = (error: any, defaultMessage: string = 'Something went wrong 😔'): string => {
    if (!error) throw new Error("Not a valid error");
    if (Array.isArray(error) && error.length > 0) {
        return getErrorMessage(error[0]);
    } else if (typeof error === 'string') {
        return error;
    } else if (error.response?.data?.error?.message) {
        return error.response.data.error.message;
    } else if (error.response?.data?.error) {
        return error.response.data.error;
    } else if (error.response?.data?.message) {
        return error.response.data.message;
    } else if (error.message) {
        return error.message;
    } else {
        return defaultMessage;
    }
}

// export const parseFormFieldError = (errors: FormFieldErrors): ParsedFormFieldErrors => {
//     let errors_: ParsedFormFieldErrors = {};

//     if (errors && typeof errors === 'object') {
//         for (let [field, errorsArr] of Object.values(errors)) {
//             // Return first error related to a field only
//             errors_[field] = errorsArr[0];
//         }
//     }

//     return errors_;
// }

export const generateUniqueId = (): string => {
    return String(Date.now()); // Generates a unique 36-char ID (UUID v4)
};

export const formatNumber = (number: number): string => {
    if(!number) return '-';
    const decimals = 2;
    const valueIfNaN = '-';
    const orderSuffixes = ['', 'k', 'M', 'B', 'T'];
    const minOrder = 0;
    const maxOrder = Infinity;

    let x = parseFloat(number.toString());

    if (isNaN(x)) return valueIfNaN;

    let order;
    if (!isFinite(x)) order = 0;
    else {
        const unboundedOrder = Math.floor(Math.log10(Math.abs(x)) / 3);
        order = Math.max(0, minOrder, Math.min(unboundedOrder, maxOrder, orderSuffixes.length - 1));
    }

    const orderSuffix = orderSuffixes[order];
    if (order !== 0) x /= Math.pow(10, order * 3);

    const style: '' | '$' = ''; // Declare the type explicitly here
    return (
        (style !== '' ? '$' : '') +
        x.toLocaleString('en-US', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: decimals,
        }) +
        orderSuffix
    );
};

export const toValidNumber = (input: any): number => {
    // Define invalid values
    const invalidValues = [undefined, null, 0, "", "-"];

    // Return 0 if input is in the invalid values
    if (invalidValues.includes(input)) {
        return 0;
    }

    // Convert input to a number
    const num = Number(input);

    // Return valid number or 0 if conversion fails (NaN case)
    return isNaN(num) ? 0 : num;
}

export const isValidMobileNumber = (number: string): boolean => {
    if (!number || number.length !== 10 || isNaN(Number(number))) return false;
    return true;
}


export const isValidOTP = (otp: string[], digitsCount = 6): boolean => {
    const otpString = otp.join('').trim();
    if (otpString.length === digitsCount) {
        return true;
    }
    return false;
}

export const loadScript = (src: string): Promise<boolean> => {
    return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};


export const isFresh = (fetched_at: number | null | undefined, staleTime: number): boolean => {
    if (!fetched_at) return false;
    return (Date.now() < fetched_at + staleTime);
}

export const isValidURL = (text: string): boolean => {
    try {
        const value = text.trim();
        if (!value) return false;

        // Malformed protocol like "https:/www.amazon.in" should fail
        if (/^https?:\/(?!\/)/i.test(value)) {
            return false;
        }

        // Allow bare hostnames (e.g. "www.amazon.in") by assuming https
        const hasProtocol = /^https?:\/\//i.test(value);
        const parsed = new URL(hasProtocol ? value : `https://${value}`);

        // Must be http or https
        const isHttp = ['http:', 'https:'].includes(parsed.protocol);

        // Require a real hostname with a TLD (rejects "amazon.", "amazon", "localhost")
        const hostname = parsed.hostname.replace(/\.$/, '');
        const labels = hostname.split('.');
        const hasValidHostname =
            labels.length >= 2 &&
            labels.every((label) => label.length > 0) &&
            labels[labels.length - 1].length >= 2;

        return isHttp && hasValidHostname;
    } catch {
        return false;
    }
}

export const copyToClipboard = async (text: string, toastMsg: string | null = null) => {
    try {
        await navigator.clipboard.writeText(text);
        if (toastMsg)
            toast.success(toastMsg);
    } catch (error) {
        console.log(error);
    }
}


export const generateUniqueKey = (obj: any, exclude: string[] = []): string => {
    const sortedKeys = Object.keys(obj).filter((k) => !exclude.includes(k)).sort();
    const sortedObjString = sortedKeys.map(key => `${key}:${JSON.stringify(obj[key])}`).join('|');

    const hash = CryptoJS.SHA256(sortedObjString).toString(CryptoJS.enc.Hex);
    return hash;
}

export const formatListAsString = (list: string[]): string => {
    if (list.length === 0) return '';
    if (list.length === 1) return list[0];
    if (list.length === 2) return list.join(' & ');
    return `${list.slice(0, -1).join(', ')} & ${list[list.length - 1]}`;
}


export const formatMediaTypes = (content_media_types: any) =>
    Object.entries(content_media_types)
        .map(([type, count]) => {
            const num = Number(count);
            if (!num) return null;

            let label = "";
            switch (type) {
                case "REELS":
                    label = num === 1 ? "Reel" : "Reels";
                    break;
                case "FEED":
                    label = num === 1 ? "Post" : "Posts";
                    break;
                case "STORY":
                    label = num === 1 ? "Story" : "Stories";
                    break;
            }

            return `${num} ${label}`;
        })
        .filter(Boolean)
        .join(", ");


export const getMediaTypeFromURL = (url: string): 'image' | 'video' | 'audio' | 'document' | 'unknown' => {
    const extension = url.split('?')[0].split('.').pop()?.toLowerCase();

    if (!extension) return 'unknown';

    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
    const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'flv', 'mkv', 'm4v'];
    const audioExtensions = ['mp3', 'wav', 'aac', 'flac', 'm4a', 'oga'];
    const documentExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'];

    if (imageExtensions.includes(extension)) return 'image';
    if (videoExtensions.includes(extension)) return 'video';
    if (audioExtensions.includes(extension)) return 'audio';
    if (documentExtensions.includes(extension)) return 'document';

    return 'unknown';
}

export function formatBytes(bytes: number | string | undefined | null, decimals = 2): string {
    if (!bytes) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB']
    const i = Math.floor(Math.log(Number(bytes)) / Math.log(k))

    const size = parseFloat((Number(bytes) / Math.pow(k, i)).toFixed(decimals))
    return `${size} ${sizes[i]}`
}

export const generateGoogleMapUrl = (
    lat: number,
    lng: number,
    placeId: string | null = null,
    name: string = 'Location'
): string => {
    if (placeId) {
        const baseUrl = 'https://www.google.com/maps/search/';
        const params = new URLSearchParams({
            api: '1',
            query: name,
            query_place_id: placeId,
        });
        return `${baseUrl}?${params.toString()}`;
    }

    return `https://www.google.com/maps?q=${lat},${lng}`;
};


export const getCombinedAddress = (address: SavedAddress | undefined | null) => {

    if (address) {
        const combinedAddress = `${address.premise ? address.premise + ',' : ''} ${address.sublocality ? address.sublocality + ',' : ''} ${address.city ? address.city + ',' : ''} ${address.region ? address.region + ',' : ''} ${address.pincode}`;
        return combinedAddress;
    }

    return "";
}

export const safeTextFromHTML = (html: string) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || '';
}

export const scrollToElement = (id: string, block: 'start' | 'center' | 'nearest' = 'center') => {
    const element = document?.getElementById(id);
    if (element) {
        element.scrollIntoView({
            behavior: 'smooth', // or 'auto'
            block
        });
    }
}


// Base64 encode a JS object using UTF-8 encoding
export const encodeBase64 = (obj: any): string => {
    const json = JSON.stringify(obj);
    const bytes = new TextEncoder().encode(json);
    const base64 = btoa(String.fromCharCode(...Array.from(bytes)));
    return base64;
};

// Base64 decode to JS object using UTF-8 decoding
export const decodeBase64 = (base64: string, fallbackValue: any = {}): any => {
    try {
        const binary = atob(base64);
        const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
        const json = new TextDecoder().decode(bytes);
        return JSON.parse(json);
    } catch (error) {
        return fallbackValue;
    }
};

export const formatTime = (
    ms: number,
    unit: 'second' | 'minute' | 'hour' = 'second'
  ): number => {
    const timeInSeconds = ms / 1000;
  
    switch (unit) {
      case 'minute':
        return Math.round(timeInSeconds / 60);
      case 'hour':
        return Math.round(timeInSeconds / 3600);
      case 'second':
      default:
        return Math.round(timeInSeconds);
    }
  }
  
export const isset = (value: any) => {
    if(value === undefined || value === '' || value === null) return false;
    return true;
}

export const slugify = (text: string) => {
  // Converts a string to a slug by replacing spaces with underscores, lowercasing, and removing special characters except underscores
  if (typeof text !== 'string') return '';
  return text
    .toString()
    .normalize('NFKD') // Normalize accented characters
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/[^a-z0-9_]/g, '') // Remove all non-alphanumeric and non-underscore chars
    ;
}

export function formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    const days = Math.floor(seconds / (3600 * 24));
    const hours = Math.floor((seconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const parts: string[] = [];
    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);
    if (secs) parts.push(`${secs}s`);

    return parts.join(" ");
}

export const trimQueryParamsFromURL = (url: string) => {
    return url.split(/[?#]/)[0];
}

export const formatIGPostLink = (link: string) => {
    return link[link.length-1] !== '/' ? link+'/' : link;
}

export function uniqueByKey<T>(arr: T[], key: keyof T): T[] {
    const seen = new Map<any, T>();
  
    for (const item of arr) {
      seen.set(item[key], item); 
      // overwrites earlier entry, so the latest stays
    }
  
    return Array.from(seen.values());
  }

  export const extractOrigin = (url: string): string | null => {
    try {
        const parsedUrl = new URL(url);
        let hostname = parsedUrl.hostname;

        // Remove 'www.' if present
        if (hostname.startsWith('www.')) {
            hostname = hostname.slice(4);
        }

        return hostname;
    } catch (error) {
        console.error('Invalid URL:', error);
        return null;
    }
}

export const isEmpty = (data: any): boolean => {
    if(data === '' || data === null || data === undefined) return true;
    if(Array.isArray(data) && data.length === 0) return true;
    if(typeof data === 'object' && Object.keys(data).length === 0) return true;
    return false;
}

export const formatInstagramTime = (isoDate: string): string => {
  const now = new Date();
  const date = new Date(isoDate);

  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds <= 0) {
    return 'now';
  }

  if (diffInSeconds < 60) {
    return `${diffInSeconds}s`;
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 5) {
    return `${diffInWeeks}w`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths}mo`;
  }

  const diffInYears = Math.floor(diffInDays / 365);
  return `${diffInYears}y`;
};


export const getTrimmedText = (text: string, length: number = 100): string => {
    let cleanedText = text.replaceAll("\n", " ")
    return cleanedText.length > length ? cleanedText.replaceAll('/n', ' ').substring(0, length) + '...' : cleanedText.replaceAll('/n', ' ');
}

export const getSocialAccountProfileURL = (handle: string, platform: string) => {
    if(platform === 'yt'){
        return `https://youtube.com/@${handle}`
    }else if(platform === 'ig'){
        return `https://instagram.com/${handle}`
    } else {
        return `#`
    }
}

export const stringToColor = (str: string): string => {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += value.toString(16).padStart(2, "0");
  }

  return color;
};


export const formatMonthYear = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleString("en-US", { month: "short", year: "numeric" });
};


export const hexToRgba = (hex: string, alpha = 1): string => {
  const cleanHex = hex.replace("#", "");

  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const getOrdinal = (n: number) => {
    if (n === 1) return "st";
    if (n === 2) return "nd";
    if (n === 3) return "rd";
    return "th";
};

export const shadeColor = (hex: string, percent: number) => {
    let num = parseInt(hex.replace("#", ""), 16);

    let r = (num >> 16) + percent;
    let g = ((num >> 8) & 0x00ff) + percent;
    let b = (num & 0x0000ff) + percent;

    r = Math.min(255, Math.max(0, r));
    g = Math.min(255, Math.max(0, g));
    b = Math.min(255, Math.max(0, b));

    return (
        "#" +
        (r << 16 | g << 8 | b)
            .toString(16)
            .padStart(6, "0")
    );
};

export const isOfficialEmail = (email: string) => {
    const blacklistedDomains = [
        "gmail.",
        "googlemail.",
        "yahoo.",
        "outlook.",
        "hotmail.",
        "icloud.",
        "proton.",
        "aol.",
        "yopmail.",
        "live.",
        "livemail.",
        "msn.",
        "msnmail.",
        "mailinator",
        "tempmail.",
        "10minutemail.",
        "throwawaymail.",
        "guerrillamail.",
        "temp-mail.",
        "zohomail.",
        "protonmail."
    ];

    const domain = email.split("@")[1]?.toLowerCase();

    if (!domain) return false;

    return !blacklistedDomains.some(provider =>
        domain.includes(provider)
    );
};

export const formatAmount = (
    amount: any,
    currency: string = "INR",
    symbol: string = "₹",
    locale: string = "en-IN"
): string => {
    const value = toValidNumber(amount);
    const safeCurrency = currency || "INR";
    const safeSymbol = symbol || "₹";
    const number = isNaN(value) ? 0 : value;
    const formatted = new Intl.NumberFormat(locale, {
        style: "currency",
        currency: safeCurrency,
        currencyDisplay: "code",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(number);
    return formatted.replace(safeCurrency, safeSymbol).trim();
};
